import type { HeapSnapshotOptions } from 'node:v8'

type Imports = {
  v8: typeof import('v8')
  vm: typeof import('vm')
}

let imports: Imports | null = null

/**
 * Because code can run in node and browser, we use dynamic imports.
 */
async function setupImports() {
  try {
    const v8 = await import('v8')
    const vm = await import('vm')
    imports = {
      v8,
      vm,
    }
  } catch (e) {
    console.error(e)
  }
}
export const IMPORT_SETUP = setupImports()

export type HeapStats = {
  total: number
  limit: number
}

/**
 * See: https://nodejs.org/api/v8.html#v8getheapstatistics
 */
export function currentHeapStatsSave(): HeapStats {
  if (imports?.v8.getHeapStatistics) {
    const stats = imports?.v8.getHeapStatistics()
    return {
      total: stats.total_heap_size,
      limit: stats.heap_size_limit,
    }
  }
  return {
    total: 0,
    limit: 0,
  }
}

/**
 * See:
 * - https://stackoverflow.com/a/75007985
 * - https://nodejs.org/learn/diagnostics/memory/understanding-and-tuning-memory#expose-gc
 */
export function triggerGCSave(major = true) {
  try {
    imports?.v8.setFlagsFromString('--expose_gc')
    const gc = imports?.vm.runInNewContext('gc') // nocommit
    gc(major) // Online some1 said that multiple calls are sometimes more effective
  } catch {
    /* empty */
  }
}

/**
 * Writes a .heapsnapshot file to the current working directory.
 * This can be debugged with any chromium memory debugger
 *
 * See:
 * - https://nodejs.org/api/v8.html#v8writeheapsnapshotfilenameoptions
 * - https://developer.chrome.com/docs/devtools/memory-problems/heap-snapshots
 *
 * Hint:
 *  It's best to call this before and after memory intensive operations and then
 *  compare both in the memory debugger.
 */
export function writeHeapSnapshotSave(filename?: string, options?: HeapSnapshotOptions) {
  imports?.v8.writeHeapSnapshot(filename ? `${filename}.heapsnapshot` : undefined, options)
}
